// The relation strip of the diplomacy screen, as runs: a stretch of months in one relation is one
// segment, so the bar has as many segments as the relation changed times.
//
//   months  ally ally ally war war neutral      runs  [ally ×3] [war ×2] [neutral ×1]

import type { SourceId } from '../core/schema.ts';
import type { Relation } from '../world/api.ts';
import type { RelationMonth } from './extensions.ts';
import { fmtMonth } from './format.ts';

export interface RelationRun {
  relation: Relation;
  /** 'YYYY-MM' of the first and last month of the run. */
  from: string;
  to: string;
  months: number;
  /** The rule and sources of the run's first month: why the relation became this. */
  reason: string;
  sources: SourceId[];
}

export function relationRuns(months: RelationMonth[]): RelationRun[] {
  const runs: RelationRun[] = [];

  for (const m of months) {
    const last = runs[runs.length - 1];
    if (last && last.relation === m.relation) {
      last.to = m.month;
      last.months++;
      continue;
    }
    runs.push({ relation: m.relation, from: m.month, to: m.month, months: 1, reason: m.reason, sources: m.sources });
  }
  return runs;
}

/**
 * When a run held, in words. Only the run still on is "since"; a past run gets both ends. A run
 * that opens the history began no later than the first month we have, so it claims no start.
 *
 *   past      Mar 2003 – Nov 2011     until Nov 2011 (opens the history)
 *   current   since Mar 2014          throughout (opens the history)
 */
export function runDates(run: RelationRun, which: 'current' | 'past', firstMonth: string): string {
  const opens = run.from === firstMonth;
  if (which === 'current') {
    return opens ? 'throughout' : `since ${fmtMonth(run.from)}`;
  }
  if (opens) {
    return `until ${fmtMonth(run.to)}`;
  }
  return run.to === run.from ? fmtMonth(run.from) : `${fmtMonth(run.from)} – ${fmtMonth(run.to)}`;
}
